import { createRoute, redirect } from "@tanstack/react-router"
import type { ReactNode } from "react"

import { InviteScreen } from "../../screens/auth/invite-screen"
import { ForgotPasswordScreen } from "../../screens/auth/forgot-password-screen"
import { LoginScreen } from "../../screens/auth/login-screen"
import { ResetPasswordScreen } from "../../screens/auth/reset-password-screen"
import { CreateSalonScreen } from "../../screens/directories/onboarding/create-salon-screen"
import { EnrollMfaScreen } from "../../screens/login/enroll-mfa-screen"
import { MfaScreen } from "../../screens/login/mfa-screen"
import { ChooseTenantScreen } from "../../screens/tenant/choose-tenant-screen"
import { rootRoute } from "./root-route"

/**
 * Экраны до входа в салон. У каждого шага свой адрес — второй фактор и выбор салона
 * не делаются окном поверх формы: у них своё состояние, свой отказ и свой возврат назад
 * (ТЗ §4).
 */

export interface NextSearch {
  /** Куда вернуть человека после входа. Проверяется в safeNextPath — это значение снаружи. */
  readonly next?: string | undefined
}

function readNext(search: Record<string, unknown>): NextSearch {
  return { next: typeof search["next"] === "string" ? search["next"] : undefined }
}

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: readNext,
  component: function LoginRouteComponent(): ReactNode {
    const { next } = loginRoute.useSearch()
    return <LoginScreen next={next} />
  },
})

/**
 * 1-Э1.3. Запрос восстановления — свой адрес: ссылку «вот сюда жми, если не помнишь
 * пароль» присылают человеку в переписке, и она обязана открыть ту же страницу.
 */
export const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  validateSearch: (search: Record<string, unknown>): { email?: string | undefined } => ({
    email: typeof search["email"] === "string" ? search["email"] : undefined,
  }),
  component: function ForgotPasswordRouteComponent(): ReactNode {
    const { email } = forgotPasswordRoute.useSearch()
    return <ForgotPasswordScreen email={email} />
  },
})

/** Новый пароль по ссылке из письма. Код приходит в адресе и нигде не сохраняется. */
export const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  validateSearch: (search: Record<string, unknown>): { token?: string | undefined } => ({
    token: typeof search["token"] === "string" ? search["token"] : undefined,
  }),
  component: function ResetPasswordRouteComponent(): ReactNode {
    const { token } = resetPasswordRoute.useSearch()
    return <ResetPasswordScreen token={token} />
  },
})

export const mfaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login/mfa",
  validateSearch: readNext,
  component: function MfaRouteComponent(): ReactNode {
    const { next } = mfaRoute.useSearch()
    return <MfaScreen next={next} />
  },
})

/**
 * Э1.2-б. Заведение второго фактора ПРИ ВХОДЕ — свой адрес по той же причине, что
 * и у ввода кода: у шага своё состояние (выпущенный секрет), свой отказ и свой конец
 * (показанные один раз резервные коды). Внутрь кабинета с этого шага уходят только
 * после того, как человек подтвердил, что коды сохранил.
 */
export const enrollMfaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login/mfa/setup",
  validateSearch: readNext,
  component: function EnrollMfaRouteComponent(): ReactNode {
    const { next } = enrollMfaRoute.useSearch()
    return <EnrollMfaScreen next={next} />
  },
})

/**
 * 4-А1 «Создать салон» — ДО входа и потому вне защищённой области: у человека, который
 * открывает свой первый салон, ещё нет ни учётной записи, ни салона, и требовать от него
 * войти было бы кругом. Ссылка на этот адрес стоит в подвале экрана входа.
 */
export const createSalonRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/create-salon",
  component: CreateSalonScreen,
})

export const chooseTenantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/choose-salon",
  validateSearch: readNext,
  beforeLoad: async ({ context, search }) => {
    // Выбор салона требует ВОШЕДШЕГО человека, но не требует выбранного салона —
    // именно поэтому экран живёт вне защищённой области кабинета.
    const state = await context.access.ensureLoaded()
    if (state.kind === "anonymous") {
      throw redirect({ to: "/login", search: { next: search.next } })
    }
  },
  component: function ChooseTenantRouteComponent(): ReactNode {
    const { next } = chooseTenantRoute.useSearch()
    return <ChooseTenantScreen next={next} />
  },
})

/**
 * 1-Э1.5 «Принятие приглашения» — ссылка из сообщения. ВНЕ защищённой области: по ней
 * приходит человек, у которого учётки ещё нет, — и вошедший тоже (он принимает приглашение
 * из-под своего входа, выбранный салон ему для этого не нужен).
 */
export interface InviteSearch {
  readonly token?: string | undefined
}

export const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite",
  validateSearch: (search: Record<string, unknown>): InviteSearch => ({
    token: typeof search["token"] === "string" ? search["token"] : undefined,
  }),
  component: function InviteRouteComponent(): ReactNode {
    const { token } = inviteRoute.useSearch()
    return <InviteScreen token={token} />
  },
})
